/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as React from "react";
import Checkbox from "primitiveComponents/form/Checkbox/Checkbox";
import AuthProvider from "../AuthProvider";
import { client, repository, session } from "clientInstance";
import { AuthenticationProviderElement } from "client/authentication";
import { UserResource, LoginCommand, LoginState, OctopusError, FeaturesConfigurationResource } from "client/resources";
import URI from "urijs";
import Logger from "client/logger";
import cn from "classnames";
import { Text } from "components/form";
import { ActionButton, ActionButtonType } from "components/Button";
import Environment from "environment";
import loginStateCalculator from "./loginStateCalculator";
import ErrorPanel from "components/ErrorPanel/ErrorPanel";
import BaseComponent from "components/BaseComponent";
import ExternalLink from "components/Navigation/ExternalLink";
const styles = require("./style.less");
import PageTitleHelper from "utils/PageTitleHelper";
import { TextInput } from "primitiveComponents/form/Text/Text";
import InternalRedirect from "../../../components/Navigation/InternalRedirect/InternalRedirect";
import routeLinks from "../../../routeLinks";
import { RouteComponentProps, StaticContext } from "react-router";
import { StarFish } from "../../../components/Images/SignIn/StarFish";
import { Octopus } from "../../../components/Images/Resources/Octopus";
import AuthenticationError from "client/resources/authenticationError";
import BusyIndicator from "components/BusyIndicator";
import { locationStateIncludesFrom } from "components/SecureRoute/localStateIncludedFrom";

interface SignInState {
    redirectToReferrer: boolean;
    userName: string;
    password: string;
    rememberMe: boolean;
    loginState: LoginState;
    showAccountTypeSelector: boolean;
    busyIndicator?: Promise<void>;
    shouldAutoSignIn: boolean;
    autoSignInProviderName: string;
    authenticationError?: AuthenticationError;
    hasLoadedAuthProviders: boolean;
}

interface SignInProps extends RouteComponentProps {
    onSessionStarted?: (user: UserResource, features: FeaturesConfigurationResource) => Promise<void>;
}

export default class SignIn extends BaseComponent<SignInProps, SignInState> {
    private authenticationProviders: AuthenticationProviderElement[] = undefined!;
    private anyFormsAuthenticationProviders: boolean = undefined!;
    private anyAuthenticationProviders: boolean = undefined!;
    private formsProvidersWithLinks: AuthenticationProviderElement[] = undefined!;
    private anyNonFormsAuthenticationProviders: boolean = undefined!;
    private nonFormsAuthenticationProviders: AuthenticationProviderElement[] = undefined!;
    private isGuestProviderEnabled: boolean = undefined!;
    private passwordField: TextInput | null = null;

    constructor(props: SignInProps) {
        super(props);
        PageTitleHelper.setRootPageTitle();

        // The from here is referring to the state provided by SecureRoute, which is `History.Location`.
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions,@typescript-eslint/no-explicit-any
        const { from } = this.props.location && this.props.location.state ? (this.props.location.state as any) : { from: { pathname: "/" } };

        this.state = {
            redirectToReferrer: false,
            showAccountTypeSelector: false,
            shouldAutoSignIn: false,
            autoSignInProviderName: "",
            userName: "",
            password: "",
            rememberMe: false,
            loginState: this.calculateLoginState(from.pathname + (from.search || "")),
            hasLoadedAuthProviders: false,
        };
    }

    async componentWillReceiveProps(nextProps: SignInProps) {
        await this.connectClient();
    }

    async componentDidMount() {
        const error = this.getErrorFromCookie();
        if (error) {
            this.onExternalAuthenticationProviderError(error);
        }

        await this.connectClient();
    }

    getErrorFromCookie(): AuthenticationError | null {
        const errors = document.cookie
            .split(";")
            .filter((c) => {
                return c.trim().indexOf("Octopus-Auth-Error") === 0;
            })
            .map((c) => {
                return decodeURIComponent(c.trim().substring("Octopus-Auth-Error=".length));
            });

        if (errors.length > 0 && errors[0]) {
            const errorObj = JSON.parse(errors[0]);
            return errorObj;
        }
        return null;
    }

    async connectClient() {
        const alreadyAuthenticated = await this.continueIfAlreadyAuthenticated();
        if (!alreadyAuthenticated) {
            const browserURI = URI(window.location);
            browserURI.hasQuery("error", (error: { message: string }) => {
                if (!error) {
                    return;
                }
                this.setError(error);
            });

            await this.setupAuthenticationProviders();
        }
    }

    render() {
        const { from } = locationStateIncludesFrom(this.props.location) ? this.props.location.state : { from: { pathname: routeLinks.root } };

        if (this.state.redirectToReferrer) {
            return <InternalRedirect to={from} />;
        }

        return (
            <div className={styles.container}>
                {!this.state.hasLoadedAuthProviders ? (
                    <div>
                        <BusyIndicator show={true} inline={true} />
                    </div>
                ) : !this.anyAuthenticationProviders ? (
                    this.noAuthenticationProvidersEnabled()
                ) : (
                    this.renderSignIn()
                )}
            </div>
        );
    }

    renderSignIn() {
        return (
            <div>
                {this.state.showAccountTypeSelector ? (
                    this.accountTypeSelection()
                ) : (
                    <div>
                        <div className={styles.content}>
                            <div className={styles.logo}>
                                <div>
                                    <em className="fontoctopus-octopus" />
                                </div>
                                {!this.state.authenticationError && <div>{this.state.shouldAutoSignIn ? <h4>Signing in, please wait...</h4> : <h4>Welcome! Please sign in.</h4>}</div>}
                            </div>
                            {this.state.authenticationError && this.showError(this.state.authenticationError)}
                            {(this.anyFormsAuthenticationProviders || this.formsProvidersWithLinks.length > 0) && (
                                <div>
                                    {this.anyFormsAuthenticationProviders && (
                                        <form onSubmit={this.signIn} className={styles.form}>
                                            <Text value={this.state.userName} label="Username" onChange={(userName) => this.setState({ userName })} autoFocus={true} id="userName" />
                                            <Text
                                                label="Password"
                                                textInputRef={(ref) => (this.passwordField = ref)}
                                                value={this.state.password}
                                                id="password"
                                                type="password"
                                                onChange={(password) => this.setState({ password })}
                                                autoComplete="new-password"
                                            />
                                            <Checkbox label="Remember me on this computer" onChange={(rememberMe) => this.setState({ rememberMe })} value={this.state.rememberMe} className={styles.rememberMe} />
                                            <ActionButton type={ActionButtonType.Primary} label="SIGN IN" busyLabel="SIGNING IN..." onClick={this.signIn} />
                                        </form>
                                    )}
                                    {this.formsProvidersWithLinks.map((p) => (
                                        <div className={styles.externalAuthProvider} key={p.Name}>
                                            <AuthProvider
                                                provider={p}
                                                shouldAutoSignIn={this.state.shouldAutoSignIn}
                                                autoSignInProviderName={this.state.autoSignInProviderName}
                                                loginState={this.state.loginState}
                                                onError={this.onExternalAuthenticationProviderError}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                            {this.anyNonFormsAuthenticationProviders && (
                                <div className={styles.externalNonFormsProviders}>
                                    {this.nonFormsAuthenticationProviders.map((p) => (
                                        <div className={styles.externalAuthProvider} key={p.Name}>
                                            <AuthProvider
                                                provider={p}
                                                shouldAutoSignIn={this.state.shouldAutoSignIn}
                                                autoSignInProviderName={this.state.autoSignInProviderName}
                                                loginState={this.state.loginState}
                                                onError={this.onExternalAuthenticationProviderError}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {this.isGuestProviderEnabled && (
                            <div className={styles.guestProvider} onClick={this.signInAsGuest}>
                                <a>I AM A GUEST</a>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    private setError(error: { ErrorMessage?: string; Errors?: string[]; DetailLinks?: string[]; message?: string }) {
        if (!error) {
            return;
        }

        Logger.error(error);

        const authenticationError: AuthenticationError = {
            ErrorMessage: error.ErrorMessage || error.message || error.toString(),
            Errors: error.Errors || [],
            DetailLinks: error.DetailLinks || [],
        };

        this.setState({ authenticationError });
    }

    private accountTypeSelection() {
        return (
            <div className={cn(styles.accountTypeSelection, styles.areas)}>
                <div className={styles.guestAccount} onClick={this.signInAsGuest}>
                    <StarFish height="4rem" className={styles.accountTypeIcon} />
                    <h2>I am a guest</h2>
                </div>
                <div className={styles.authenticatedAccount} onClick={this.hideAccountTypeSelector}>
                    <Octopus height="4rem" className={styles.accountTypeIcon} />
                    <h2>I have an account</h2>
                </div>
            </div>
        );
    }

    private async setupAuthenticationProviders() {
        const document = await repository.Authentication.get();
        this.authenticationProviders = document.AuthenticationProviders;

        this.nonFormsAuthenticationProviders = this.authenticationProviders.filter((p) => {
            return !p.FormsLoginEnabled && !this.isActiveDirectory(p) && p.IdentityType !== "Guest";
        });

        const formsAuthenticationProviders = this.authenticationProviders.filter((p) => {
            return p.FormsLoginEnabled && p.IdentityType !== "Guest";
        });
        this.anyFormsAuthenticationProviders = formsAuthenticationProviders.length > 0;
        this.anyNonFormsAuthenticationProviders = this.nonFormsAuthenticationProviders.length > 0;

        this.formsProvidersWithLinks = this.authenticationProviders.filter(this.isActiveDirectory);

        this.anyAuthenticationProviders = this.anyFormsAuthenticationProviders || this.anyNonFormsAuthenticationProviders || this.formsProvidersWithLinks.length > 0;

        this.isGuestProviderEnabled = this.authenticationProviders.filter((p) => p.IdentityType === "Guest").length > 0;

        const singleNonFormsAuthenticationProvider = !this.anyFormsAuthenticationProviders && this.nonFormsAuthenticationProviders.length + this.formsProvidersWithLinks.length === 1;
        const showAccountTypeSelector = this.isGuestProviderEnabled && !this.state.authenticationError;
        let shouldAutoSignIn = document.AutoLoginEnabled && !this.state.authenticationError && singleNonFormsAuthenticationProvider && !showAccountTypeSelector;
        let autoSignInProviderName = "";
        if (shouldAutoSignIn) {
            autoSignInProviderName = this.authenticationProviders[0].Name;
        }

        if (this.props.location.search) {
            const provider = await repository.Authentication.wasLoginInitiated(this.props.location.search);

            if (provider && provider.WasLoginInitiated) {
                shouldAutoSignIn = true;
                autoSignInProviderName = provider.ProviderName;
                if (provider.ProviderName === "Octopus - Guest") {
                    await this.signInAsGuest();
                }
            }
        }

        this.setState({ showAccountTypeSelector, shouldAutoSignIn, autoSignInProviderName, hasLoadedAuthProviders: true });
    }

    private async authenticationSucceeded(user: UserResource) {
        await this.startSession(user);
        this.setState({ redirectToReferrer: true });
    }

    private authenticate = async (userName: string, password: string, rememberMe: boolean) => {
        try {
            Logger.log(`Attempting to sign in: ${userName}`);
            const loginCommand: LoginCommand = {
                Username: userName,
                Password: password,
                RememberMe: rememberMe,
                State: this.state.loginState,
            };
            const user = await repository.Users.signIn(loginCommand);

            await this.authenticationSucceeded(user);
        } catch (error) {
            this.setError(error);
        }
    };

    private async startSession(user: UserResource) {
        try {
            const features = await repository.FeaturesConfiguration.get();
            session.start(user, features);
            if (this.props.onSessionStarted) {
                await this.props.onSessionStarted(user, features);
            }
        } catch (error) {
            Logger.log(error);

            const message = "The sign in succeeded but we failed to get the resultant permissions for this user account. ";

            const reason = error.StatusCode === 401 ? "This can happen if the Octopus authentication cookie is blocked." : "There was a problem communicating with the Octopus Server: " + error.ErrorMessage;

            this.setError({ ErrorMessage: message + reason });
        }
    }

    private isActiveDirectory(provider: AuthenticationProviderElement) {
        return provider.Name === "Active Directory";
    }

    private onExternalAuthenticationProviderError = (error: { ErrorMessage?: string; Errors?: string[]; DetailLinks?: string[]; message?: string }) => {
        this.setError(error);
    };

    private signIn = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const busyIndicator = this.authenticate(this.state.userName, this.state.password, this.state.rememberMe);
        this.setState({ busyIndicator });
        await busyIndicator;
    };

    private signInAsGuest = async () => {
        await this.authenticate("guest", "guest", true);
    };

    private hideAccountTypeSelector = () => {
        this.setState({ showAccountTypeSelector: false });
    };

    private async continueIfAlreadyAuthenticated() {
        try {
            const user = await repository.Users.getCurrent();
            Logger.log("User already authenticated");
            await this.authenticationSucceeded(user);
            return true;
        } catch (authenticationError) {
            Logger.log(authenticationError);
        }

        return false;
    }

    private noAuthenticationProvidersEnabled() {
        return (
            <div>
                There are no authentication providers enabled. Learn about enabling <ExternalLink href="AuthenticationProviders">authentication providers</ExternalLink>
            </div>
        );
    }

    private calculateLoginState(redirectToPath: string) {
        return loginStateCalculator(location.href, redirectToPath, Environment.isInDevelopmentMode());
    }

    private showError(authenticationError: AuthenticationError) {
        return (
            <div className={styles.authenticationError}>
                <ErrorPanel message={authenticationError.ErrorMessage} errors={authenticationError.Errors} parsedHelpLinks={authenticationError.DetailLinks} />
            </div>
        );
    }
}
